import { useEffect, useState, type ReactNode } from "react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Card,
  CardContent,
  EmptyState,
  Skeleton,
  StatusBadge,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCalendarDate, formatCurrency } from "@/design-system/lib/format";
import { StatementDownloadBar } from "../components/StatementDownloadBar";
import {
  portalSellerFinanceApi,
  type SellerFinanceSummary,
  type SellerIncomePoint,
  type SellerNoteLedger,
  type SellerNoteRow,
  type SellerPaymentPeriod,
} from "../api/sellerFinance";

// External party portals — Seller "Income & accounting" (PR-B). A visual
// roll-up across the seller's notes (income received, principal still owed to
// them, broker deductions, income trend) that drills into a single note's
// schedule + per-payment detail, with each carve taken out of the seller's
// share — the broker fee and the buyer consulting fee — shown as its own line.
// Read-only over the engine's existing servicing ledger.

const STATUS_LABEL: Record<string, string> = {
  current: "On track",
  overdue: "Overdue",
  "paid-off": "Paid off",
};

const PERIOD_LABEL: Record<string, string> = {
  "on-time": "On time",
  late: "Late",
  partial: "Partial",
  missed: "Missed",
  nsf: "NSF",
  prepaid: "Prepaid",
  upcoming: "Upcoming",
};

function noteStatusTone(status: string): StatusTone {
  if (status === "overdue") return "danger";
  if (status === "paid-off") return "ok";
  return "progress";
}

function periodTone(status: string): StatusTone {
  switch (status) {
    case "on-time":
    case "prepaid":
      return "ok";
    case "late":
    case "partial":
      return "warn";
    case "missed":
    case "nsf":
      return "danger";
    default:
      return "neutral";
  }
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Card>
      <CardContent className="space-y-space-1">
        <p className="text-caption uppercase tracking-wide text-gray-mid">{label}</p>
        <div className="text-h4 font-semibold text-onyx">{children}</div>
      </CardContent>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-caption text-gray-mid">{label}</p>
      <p className="text-small font-medium text-onyx">{value}</p>
    </div>
  );
}

function IncomeTrendChart({ points }: { points: SellerIncomePoint[] }) {
  if (points.length === 0) return null;
  const data = points.map((p) => ({
    label: formatCalendarDate(p.periodDate, { month: "short", year: "2-digit" }),
    value: p.incomeReceived,
  }));
  return (
    <Card>
      <CardContent className="space-y-space-2">
        <p className="text-caption uppercase tracking-wide text-gray-mid">
          Income received over time
        </p>
        {/* currentColor keeps the series on the gold token — no hardcoded hex. */}
        <div className="h-40 w-full text-gold" data-testid="seller-income-trend">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis hide />
              <Area
                type="monotone"
                dataKey="value"
                stroke="currentColor"
                fill="currentColor"
                fillOpacity={0.15}
              />
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function NoteCard({ note, onOpen }: { note: SellerNoteRow; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`seller-note-card-${note.loanId}`}
      className="block w-full rounded-lg border border-border-light bg-white-pure p-space-4 text-left transition-colors hover:border-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
    >
      <div className="flex items-center justify-between gap-space-3">
        <span className="text-small font-semibold text-onyx">{note.loanNumber}</span>
        <StatusBadge tone={noteStatusTone(note.status)}>
          {STATUS_LABEL[note.status] ?? note.status}
        </StatusBadge>
      </div>
      <div className="mt-space-3 grid grid-cols-2 gap-space-3 sm:grid-cols-4">
        <Field label="Principal owed to you" value={formatCurrency(note.remainingPrincipal)} />
        <Field label="Income received" value={formatCurrency(note.totalIncomeReceived)} />
        <Field label="Broker deductions" value={formatCurrency(note.totalBrokerDeductions)} />
        <Field
          label="Next payment"
          value={note.nextDueDate ? formatCalendarDate(note.nextDueDate) : "—"}
        />
      </div>
    </button>
  );
}

function ScheduleTable({ schedule }: { schedule: SellerPaymentPeriod[] }) {
  if (schedule.length === 0) {
    return (
      <EmptyState
        title="No schedule yet"
        description="This note has no scheduled periods on file."
      />
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border-light">
      <table className="w-full text-small">
        <thead>
          <tr className="border-b border-border-light bg-white-warm text-left text-caption uppercase tracking-wide text-gray-mid">
            <th className="px-space-3 py-space-2 font-medium">#</th>
            <th className="px-space-3 py-space-2 font-medium">Due</th>
            <th className="px-space-3 py-space-2 text-right font-medium">Interest</th>
            <th className="px-space-3 py-space-2 text-right font-medium">Principal</th>
            <th className="px-space-3 py-space-2 text-right font-medium">Income received</th>
            <th className="px-space-3 py-space-2 text-right font-medium">Broker deduction</th>
            <th className="px-space-3 py-space-2 text-right font-medium">Consulting fee</th>
            <th className="px-space-3 py-space-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {schedule.map((p) => (
            <tr key={p.periodIndex} className="border-b border-border-light last:border-0">
              <td className="px-space-3 py-space-2 text-gray-mid">{p.periodIndex + 1}</td>
              <td className="px-space-3 py-space-2 text-onyx">{formatCalendarDate(p.dueDate)}</td>
              <td className="px-space-3 py-space-2 text-right text-gray-mid">
                {formatCurrency(p.expectedInterest)}
              </td>
              <td className="px-space-3 py-space-2 text-right text-gray-mid">
                {formatCurrency(p.expectedPrincipal)}
              </td>
              <td className="px-space-3 py-space-2 text-right text-onyx">
                {p.incomeReceived > 0 ? formatCurrency(p.incomeReceived) : "—"}
              </td>
              <td className="px-space-3 py-space-2 text-right text-gray-mid">
                {p.brokerDeduction > 0 ? `-${formatCurrency(p.brokerDeduction)}` : "—"}
              </td>
              {/* The buyer consulting fee carve, rendered exactly like the
                  broker deduction beside it — "income received" has already
                  fallen by this amount, so the line is what stops the seller
                  seeing money gone with no explanation. */}
              <td className="px-space-3 py-space-2 text-right text-gray-mid">
                {(p.consultingDeduction ?? 0) > 0
                  ? `-${formatCurrency(p.consultingDeduction ?? 0)}`
                  : "—"}
              </td>
              <td className="px-space-3 py-space-2">
                <StatusBadge tone={periodTone(p.status)}>
                  {PERIOD_LABEL[p.status] ?? p.status}
                </StatusBadge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NoteLedgerView({ loanId, onBack }: { loanId: string; onBack: () => void }) {
  const [note, setNote] = useState<SellerNoteLedger | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setNote(null);
    setError(null);
    void (async () => {
      try {
        const data = await portalSellerFinanceApi.note(loanId);
        if (!cancelled) setNote(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load this note.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loanId]);

  return (
    <div className="space-y-space-5">
      <button
        type="button"
        onClick={onBack}
        data-testid="seller-note-back"
        className="text-small font-medium text-gold hover:underline"
      >
        ← Back to all notes
      </button>
      {error && <p className="text-small text-status-error">{error}</p>}
      {!note && !error && <Skeleton className="h-40 w-full" />}
      {note && (
        <>
          <div className="flex items-center justify-between gap-space-3">
            <h3 className="text-h4 font-semibold text-onyx">{note.loanNumber}</h3>
            <StatusBadge tone={noteStatusTone(note.status)}>
              {STATUS_LABEL[note.status] ?? note.status}
            </StatusBadge>
          </div>
          <div className="grid gap-space-3 sm:grid-cols-2 lg:grid-cols-5">
            <Stat label="Principal owed to you">
              {formatCurrency(note.remainingPrincipal)}
            </Stat>
            <Stat label="Income received">{formatCurrency(note.totalIncomeReceived)}</Stat>
            <Stat label="Broker deductions">{formatCurrency(note.totalBrokerDeductions)}</Stat>
            <Stat label="Consulting fee">
              {formatCurrency(note.totalConsultingDeductions ?? 0)}
            </Stat>
            <Stat label="Next payment">
              {note.nextDueDate ? formatCalendarDate(note.nextDueDate) : "—"}
            </Stat>
          </div>
          {note.sellerInterestIsGross && (
            <p className="text-caption text-gray-mid">
              Interest is shown gross of Phoenix's servicing fee/spread and of the broker
              and consulting fee deductions; income received is your actual net
              distribution.
            </p>
          )}
          <StatementDownloadBar
            onDownload={(p) => portalSellerFinanceApi.statement(loanId, p)}
            filename={(p) => `income-statement-${note.loanNumber}-${p}.pdf`}
          />
          <IncomeTrendChart points={note.incomeTrend} />
          <ScheduleTable schedule={note.schedule} />
        </>
      )}
    </div>
  );
}

export function SellerAccountingTab() {
  const [summary, setSummary] = useState<SellerFinanceSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await portalSellerFinanceApi.summary();
        if (!cancelled) setSummary(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load your accounting.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (selectedLoanId) {
    return <NoteLedgerView loanId={selectedLoanId} onBack={() => setSelectedLoanId(null)} />;
  }

  if (error) return <p className="text-small text-status-error">{error}</p>;

  if (!summary) {
    return (
      <div
        className="grid gap-space-3 sm:grid-cols-2 lg:grid-cols-4"
        data-testid="seller-accounting-loading"
      >
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (summary.noteCount === 0) {
    return (
      <EmptyState
        title="No notes yet"
        description="When a loan you've sold is funded and boarded, your income, remaining note balance, and payment history will appear here."
      />
    );
  }

  return (
    <div className="space-y-space-5">
      <div className="grid gap-space-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Income received to date">
          {formatCurrency(summary.totalIncomeReceived)}
        </Stat>
        <Stat label="Principal owed to you">
          {formatCurrency(summary.totalRemainingPrincipal)}
        </Stat>
        <Stat label="Broker deductions">{formatCurrency(summary.totalBrokerDeductions)}</Stat>
        <Stat label="Overall status">
          <StatusBadge tone={noteStatusTone(summary.overallStatus)}>
            {STATUS_LABEL[summary.overallStatus] ?? summary.overallStatus}
          </StatusBadge>
        </Stat>
      </div>

      <IncomeTrendChart points={summary.incomeTrend} />

      <div className="space-y-space-3">
        <h3 className="text-small font-semibold uppercase tracking-wide text-gray-mid">
          Your notes ({summary.noteCount})
        </h3>
        {summary.notes.map((note) => (
          <NoteCard key={note.loanId} note={note} onOpen={() => setSelectedLoanId(note.loanId)} />
        ))}
      </div>
    </div>
  );
}
